import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class PshipPtrService {

  constructor(private http: HttpClient) { }

  checkLockStatus(
    baseUrl: string,
    lockParams: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    // Build params exactly like original AngularJS with proper fallbacks
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add lock specific parameters
    params.action_code = lockParams.action_code;
    params.p_scenario = lockParams.p_scenario;
    params.p_jcd_key = lockParams.p_jcd_key;
    
    // Essential parameters with fallbacks
    params.tax_year = filterParams.tax_year || params.tax_year;
    params.scenario = filterParams.scenario || params.scenario;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || 250;

    // Build query string for GET request
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    // Use GET request like in original AngularJS
    return this.http.get<any>(url, { withCredentials: true });
  }

  getPartnerDetails(
    baseUrl: string,
    requestParams: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    // Build params exactly like original AngularJS with proper fallbacks
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add request specific parameters
    params.action_code = requestParams.action_code;
    params.p_ptrship_cc_key = requestParams.p_ptrship_cc_key;
    
    // Support both normal mode (tag_key) and BEAT mode (gie_key)
    if (requestParams.p_ptrship_tag_key) {
      params.p_ptrship_tag_key = requestParams.p_ptrship_tag_key;
    }
    if (requestParams.p_ptrship_gie_key) {
      params.p_ptrship_gie_key = requestParams.p_ptrship_gie_key;
    }
    
    params.p_scenario = requestParams.p_scenario;
    params.p_jcd_key = requestParams.p_jcd_key;
    
    // Essential parameters with fallbacks
    params.tax_year = filterParams.tax_year || params.tax_year;
    params.scenario = filterParams.scenario || params.scenario;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || 250;

    // Build query string for GET request
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    console.log('Making GET request to (getPartnerDetails):', url);
    console.log('Built params:', params);
    
    // Use GET request like in original AngularJS
    return this.http.get<any>(url, { withCredentials: true });
  }

  savePshipPtrForm(
    baseUrl: string,
    pshipPtrDetails: any[],
    settingsData: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    // Build payload EXACTLY like original AngularJS with proper fallbacks
    let params = _.merge({}, globalService || {}, filterParams);

    // Essential parameters with fallbacks
    params.clob_data = JSON.stringify(pshipPtrDetails);
    params.clob_settings = JSON.stringify(settingsData);  // Note: singular "settings" to match AngularJS
    params.tax_year = filterParams.tax_year || params.tax_year;
    params.scenario = filterParams.scenario || params.scenario;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";

    // Add process name
    params.process_name = "pship-ptr-grid-save";

    // Include checkLockedbj from rowData (NOT hardcoded)
    const checkLockedbj = [{
      "leid": rowData?.PTRSHIP_LEID || rowData?.LEID,
      "cdr_number": rowData?.PTRSHIP_CDR_NO || rowData?.CDR_NO,
      "consol_group_key": rowData?.CONSOL_GROUP_KEY,
      "reporting_period": rowData?.PTRSHIP_REPORTING_PERIOD || rowData?.REPORTING_PERIOD,
      "combination_key": rowData?.PTRSHIP_COMBINATION_KEY || rowData?.COMBINATION_KEY,
      "scenario_key": parseInt(filterParams.scenario)
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    const url = `${baseUrl}/saveJsonObject?action_code=hbvr78`;
    
    // CRITICAL: Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  saveBeatPtrRatio(
    baseUrl: string,
    beatPshipPtrDetails: any[],
    settingsData: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    // Build payload for BEAT mode save with action code al5qkz
    let params = _.merge({}, globalService || {}, filterParams);

    // Essential parameters
    params.clob_data = JSON.stringify(beatPshipPtrDetails);
    params.clob_settings = JSON.stringify(settingsData);
    params.tax_year = filterParams.tax_year || params.tax_year;
    params.scenario = filterParams.scenario || params.scenario;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";

    // Add process name for BEAT
    params.process_name = "beat-pship-ptr-grid-save";

    // Include checkLockedbj from rowData
    const checkLockedbj = [{
      "leid": rowData?.PTRSHIP_LEID || rowData?.LEID,
      "cdr_number": rowData?.PTRSHIP_CDR_NO || rowData?.CDR_NO,
      "consol_group_key": rowData?.CONSOL_GROUP_KEY,
      "reporting_period": rowData?.PTRSHIP_REPORTING_PERIOD || rowData?.REPORTING_PERIOD,
      "combination_key": rowData?.PTRSHIP_COMBINATION_KEY || rowData?.COMBINATION_KEY,
      "scenario_key": parseInt(filterParams.scenario)
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    const url = `${baseUrl}/saveJsonObject?action_code=al5qkz`;
    
    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    console.log('Saving BEAT ptr ratio to:', url);
    console.log('With params:', params);

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
